import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import {
  FiAlertTriangle,
  FiBarChart2,
  FiChevronsLeft,
  FiChevronsRight,
  FiChevronDown,
  FiCreditCard,
  FiFileText,
  FiHelpCircle,
  FiHome,
  FiList,
  FiPercent,
  FiPlusCircle,
  FiRepeat,
  FiRotateCcw,
  FiTrendingUp,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { NavLink } from "react-router-dom";
import assets from "../assets";

const navSections = [
  {
    label: "Payments",
    items: [
      { key: "overview", label: "Overview", icon: FiHome, path: "/payments/overview" },
          {
            key: "invoices",
            label: "Invoices",
            icon: FiFileText,
            children: [
              { key: "create-invoice", label: "Create Invoice", icon: FiPlusCircle, path: "/payments/invoices/new" },
              { key: "stripe-checkout", label: "Stripe Checkout", icon: FiCreditCard, path: "/payments/invoices/checkout" },
              { key: "all-invoices", label: "All Invoices", icon: FiList, path: "/payments/invoices" },
              { key: "payments", label: "Payments", icon: FiCreditCard, path: "/payments/invoices/payments" },
              { key: "refunds", label: "Refunds", icon: FiRotateCcw, path: "/payments/invoices/refunds" },
            ],
          },
      {
        key: "subscriptions",
        label: "Subscriptions",
        icon: FiRepeat,
        children: [
              {
                key: "create-plan",
                label: "Create Plan",
                icon: FiPlusCircle,
                path: "/payments/subscriptions/new-plan",
              },
              {
                key: "create-subscription",
                label: "Create Subscription",
                icon: FiRepeat,
                path: "/payments/subscriptions/new",
              },
              {
                key: "subscription-checkout",
                label: "Subscription Checkout",
                icon: FiCreditCard,
                path: "/payments/subscriptions/checkout",
              },
              { key: "all-plans", label: "All Plans", icon: FiTrendingUp, path: "/payments/subscriptions/plans" },
              { key: "all-subscriptions", label: "All Subscriptions", icon: FiUsers, path: "/payments/subscriptions" },
              {
                key: "subscription-customers",
                label: "Subscription Customers",
                icon: FiUser,
                path: "/payments/subscriptions/customers",
              },
              {
                key: "subscription-payments",
                label: "Payments",
                icon: FiCreditCard,
                path: "/payments/subscriptions/invoices",
              },
          {
            key: "subscription-refunds",
            label: "Refunds",
            icon: FiRotateCcw,
            path: "/payments/subscriptions/invoices/refunds",
          },
        ],
      },
      {
        key: "disputes",
        label: "Disputes",
        icon: FiAlertTriangle,
        path: "/payments/subscriptions/disputes",
      },
      {
        key: "reports",
        label: "Reports",
        icon: FiBarChart2,
        children: [
          { key: "revenue", label: "Revenue", icon: FiTrendingUp, path: "/payments/reports/revenue" },
          { key: "mrr", label: "MRR / Churn", icon: FiPercent, path: "/payments/reports/mrr-churn" },
          { key: "reports-invoices", label: "All Invoices", icon: FiFileText, path: "/payments/reports/invoices" },
          { key: "reports-subscriptions", label: "All Subscriptions", icon: FiUsers, path: "/payments/reports/subscriptions" },
          { key: "taxes", label: "Taxes", icon: FiPercent, path: "/payments/reports/taxes" },
        ],
      },
      {
        key: "analytics",
        label: "Analytics",
        icon: FiBarChart2,
        path: "/payments/analytics",
      },
      {
        key: "help",
        label: "Help",
        icon: FiHelpCircle,
        path: "/payments/help",
      },
      {
        key: "profile",
        label: "Profile",
        icon: FiUser,
        path: "/payments/profile",
      },
    ],
  },
];

const NavItem = ({
  item,
  isActive,
  isCollapsed,
  isExpandable,
  isExpanded,
  onClick,
  variant = "parent",
}) => {
  const isChild = variant === "child";
  const isLink = Boolean(item.path) && !isExpandable;
  const rowHeight = isChild ? "36px" : "44px";
  const fontSize = isChild ? "13px" : "sm";
  const fontWeight = isChild ? (isActive ? "semibold" : "normal") : isActive ? "semibold" : "medium";
  const color = isChild ? (isActive ? "blue.700" : "gray.600") : "gray.700";
  const plValue = isCollapsed ? 0 : isChild ? (isActive ? "42px" : "44px") : "16px";
  const prValue = isCollapsed ? 0 : "12px";
  const gapValue = isCollapsed ? 0 : isChild ? 2 : 3;

  const content = (
    <Flex
      as={isLink ? NavLink : "button"}
      to={isLink ? item.path : undefined}
      type={isLink ? undefined : "button"}
      onClick={onClick}
      position="relative"
      align="center"
      justify={isCollapsed ? "center" : "flex-start"}
      w="full"
      h={rowHeight}
      pl={plValue}
      pr={prValue}
      gap={gapValue}
      bg={isActive && !isChild ? "gray.100" : "transparent"}
      _hover={{ bg: "gray.50" }}
      fontWeight={fontWeight}
      color={color}
      cursor="pointer"
      borderRadius="10px"
      textDecoration="none"
      _focusVisible={{ boxShadow: "outline" }}
    >
      {isChild && isActive ? (
        <Box
          position="absolute"
          left="16px"
          top="8px"
          bottom="8px"
          w="2px"
          bg="blue.500"
          borderRadius="full"
        />
      ) : null}
      {!isChild && item.icon ? <Icon as={item.icon} boxSize="18px" color="gray.600" /> : null}
      {!isCollapsed ? <Text fontSize={fontSize}>{item.label}</Text> : null}
      {!isCollapsed && isExpandable ? (
        <Icon
          as={FiChevronDown}
          boxSize="16px"
          ml="auto"
          color="gray.500"
          transform={isExpanded ? "rotate(180deg)" : "rotate(0deg)"}
          transition="transform 0.2s ease"
        />
      ) : null}
    </Flex>
  );

  if (isCollapsed) {
    return (
      <Tooltip label={item.label} placement="right" hasArrow shouldWrapChildren>
        {content}
      </Tooltip>
    );
  }

  return content;
};

const PaymentsSidebar = ({ isCollapsed, onToggle, currentPath }) => {
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const normalizedPath = currentPath && currentPath.length > 1 ? currentPath.replace(/\/+$/, "") : currentPath;

  const getActiveChildPath = useCallback((item) => {
    if (!item.children) return null;
    const childPaths = item.children.map((child) => child.path).filter(Boolean);
    const matches = childPaths.filter(
      (path) => normalizedPath === path || normalizedPath.startsWith(`${path}/`)
    );
    if (matches.length === 0) return null;
    const longestMatch = matches.reduce((longest, path) => (path.length > longest.length ? path : longest), matches[0]);
    const shortestPath = childPaths.reduce((shortest, path) => (path.length < shortest.length ? path : shortest), childPaths[0]);
    if (longestMatch === shortestPath && normalizedPath !== shortestPath) {
      return null;
    }
    return longestMatch;
  }, [normalizedPath]);

  const activeParentKeys = useMemo(() => {
    const keys = [];
    navSections.forEach((section) => {
      section.items.forEach((item) => {
        if (!item.children) return;
        if (getActiveChildPath(item)) {
          keys.push(item.key);
        }
      });
    });
    return keys;
  }, [getActiveChildPath]);

  useEffect(() => {
    if (activeParentKeys.length === 0) return;
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      activeParentKeys.forEach((key) => next.add(key));
      return next;
    });
  }, [activeParentKeys]);

  const isItemActive = (item, activeChildPath) => {
    if (item.children) {
      return Boolean(activeChildPath);
    }
    if (!item.path) return false;
    if (normalizedPath === item.path || normalizedPath.startsWith(`${item.path}/`)) return true;
    return false;
  };

  const toggleExpanded = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleParentClick = (item) => {
    if (item.children) {
      toggleExpanded(item.key);
    }
  };

  return (
    <Flex
      direction="column"
      bg="white"
      borderRightWidth="1px"
      w={isCollapsed ? "72px" : "260px"}
      transition="width 0.2s ease"
      minH="100vh"
      position="sticky"
      top={0}
    >
      <Flex align="center" justify="space-between" px={4} py={4}>
        <HStack spacing={3}>
          <Box boxSize="32px" bg="blue.500" borderRadius="md" overflow="hidden">
            <Image src={assets.logoOnly} alt="Telivi logo" boxSize="32px" objectFit="cover" />
          </Box>
          {!isCollapsed ? (
            <Text fontWeight="bold" letterSpacing="wide">
              Telivi
            </Text>
          ) : null}
        </HStack>
        <IconButton
          aria-label="Collapse sidebar"
          icon={isCollapsed ? <FiChevronsRight /> : <FiChevronsLeft />}
          size="sm"
          variant="ghost"
          onClick={onToggle}
        />
      </Flex>
      <Divider />
      <Box flex="1" overflowY="auto" px={2} py={3}>
        {navSections.map((section) => (
          <Box key={section.label} mb={4}>
            {!isCollapsed ? (
              <Text
                fontSize="11px"
                fontWeight="600"
                letterSpacing="0.08em"
                color="gray.500"
                px={4}
                pt={4}
                pb={2}
                textTransform="uppercase"
              >
                {section.label.toUpperCase()}
              </Text>
            ) : null}
            <VStack align="stretch" spacing={1}>
              {section.items.map((item) => {
                const activeChildPath = getActiveChildPath(item);
                return (
                  <Box key={item.key} mb={item.children && expandedKeys.has(item.key) ? "6px" : 0}>
                    <NavItem
                      item={item}
                      isActive={isItemActive(item, activeChildPath)}
                      isCollapsed={isCollapsed}
                      isExpandable={Boolean(item.children)}
                      isExpanded={expandedKeys.has(item.key)}
                      onClick={() => handleParentClick(item)}
                    />
                    {!isCollapsed && item.children && expandedKeys.has(item.key) ? (
                      <VStack align="stretch" spacing={0} mt={1}>
                        {item.children.map((child) => (
                          <NavItem
                            key={child.key}
                            item={child}
                            variant="child"
                            isActive={activeChildPath === child.path}
                            isCollapsed={isCollapsed}
                          />
                        ))}
                      </VStack>
                    ) : null}
                  </Box>
                );
              })}
            </VStack>
          </Box>
        ))}
      </Box>
    </Flex>
  );
};

export default PaymentsSidebar;
